<script setup lang="ts">
import VTooltip from '@shared/component/VTooltip/VTooltip.vue';
import { FontAwesomeIcon } from '@shared/plugin/icons';
import { computed } from 'vue';
import { hasPermission } from '@permission/hasPermission';

const props = withDefaults(
  defineProps<{
    label: string;
    value: string;
    editable: boolean;
    reden?: string | null;
    newValue?: string | null;
  }>(),
  {
    reden: null,
  },
);

const emit = defineEmits<{ (e: 'edit' | 'remove'): void }>();

const magMutatieStarten = hasPermission('Muteren.MutatieStarten');
const magRoyeren = hasPermission('Muteren.Royeren');

const hasNewValue = computed(() => props.newValue && props.newValue !== props.value);
const valueClasses = computed(() => {
  if (!hasNewValue.value) {
    return 'text-grey-700 underline decoration-dashed underline-offset-4';
  }

  return 'text-neutral-500 line-through';
});

function onClick() {
  if (!props.editable) {
    return;
  }

  emit(hasNewValue.value ? 'remove' : 'edit');
}
</script>

<template>
  <div class="grid grid-cols-subgrid lg:col-span-2">
    <div class="mb-2 font-bold text-primary-900 lg:mb-0">{{ label }}</div>
    <div
      v-if="magRoyeren || magMutatieStarten"
      class="flex"
      :class="{ 'cursor-pointer': editable, 'cursor-not-allowed': !editable }"
      @click="onClick"
    >
      <span :class="valueClasses" data-testid="current-value">{{ value }}</span>
      <span class="ml-2 basis-0">
        <VTooltip :label="reden!" :active="!editable && !!reden">
          <span
            v-if="!hasNewValue"
            class="no-underline"
            :class="{ 'text-primary-900': editable, 'text-grey': !editable }"
          >
            <FontAwesomeIcon :icon="editable ? 'far fa-pen' : 'far fa-pen-slash'" />
          </span>
        </VTooltip>
        <span v-if="hasNewValue" class="whitespace-nowrap">
          <span class="font-bold text-green" data-testid="new-value">
            {{ newValue }}
          </span>
          <FontAwesomeIcon
            icon="far fa-trash"
            class="ml-2 cursor-pointer font-normal text-neutral-500"
          />
        </span>
      </span>
    </div>
    <div v-else>{{ value }}</div>
  </div>
</template>
